<template>
    <DCard
        :title="`${product.name} - ${__('products.products.show.plansAdvancedView.title')}`"
        padding
        main-class="overflow-visible"
    >
        <DRow align-vertical="end">
            <DCol
                sm="12"
                class="text-right"
            >
                <div class="list-configuration">
                    <DButton
                        id="table-configuration"
                        icon="cog-outline"
                        :label="__('messages.list_configuration')"
                        class="float-end"
                        @click="showListViewModal = true"
                    />
                </div>
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol sm="3">
                <DInput
                    id="search"
                    v-model="filter.search"
                    :label="__('products.products.show.plansAdvancedView.search')"
                    :placeholder="__('products.products.show.plansAdvancedView.search_placeholder')"
                    @keydown.enter.exact.prevent="submitFilters"
                />
            </DCol>
            <DCol sm="3">
                <DMultiSelect
                    id="targets-dropdown"
                    :options="targetOptions"
                    :label="__('products.products.show.plansAdvancedView.targets')"
                    :search="true"
                    :selection="true"
                    :selected="selectedTargets"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedTargets"
                />
            </DCol>
            <DCol sm="3">
                <DMultiSelect
                    id="market-regions-dropdown"
                    :options="marketRegionOptions"
                    :label="__('products.products.show.plansAdvancedView.market_region')"
                    :search="true"
                    :selection="true"
                    :selected="selectedMarketRegions"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedMarketRegions"
                />
            </DCol>
            <DCol sm="3">
                <DMultiSelect
                    id="currencies-dropdown"
                    :options="currencyOptions"
                    :label="__('products.products.show.plansAdvancedView.currency')"
                    :search="true"
                    :selection="true"
                    :selected="selectedCurrencies"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedCurrencies"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol sm="3">
                <DMultiSelect
                    id="runtimes-dropdown"
                    :options="runtimeOptions"
                    :label="__('products.products.show.plansAdvancedView.runtime')"
                    :search="true"
                    :selection="true"
                    :selected="selectedRuntimes"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedRuntimes"
                />
            </DCol>
            <DCol sm="3">
                <DMultiSelect
                    id="billing-cycles-dropdown"
                    :options="billingCycleOptions"
                    :label="__('products.products.show.plansAdvancedView.billing_cycle')"
                    :search="true"
                    :selection="true"
                    :selected="selectedBillingCycles"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedBillingCycles"
                />
            </DCol>
            <DCol sm="3">
                <DMultiSelect
                    id="contract-terms-dropdown"
                    :options="contractTermOptions"
                    :label="__('products.products.show.plansAdvancedView.contract_term')"
                    :search="true"
                    :selection="true"
                    :selected="selectedContractTerms"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedContractTerms"
                />
            </DCol>
            <DCol sm="3">
                <DMultiSelect
                    id="discount-types-dropdown"
                    :options="discountTypeOptions"
                    :label="__('products.products.show.plansAdvancedView.discount_type')"
                    :search="true"
                    :selection="true"
                    :selected="selectedDiscountTypes"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedDiscountTypes"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol sm="3">
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="submitFilters"
                />
                <DButton
                    id="filter-reset"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <div class="overflow-x-auto">
            <DTable
                :items="tableItems"
                :fields="getFields"
            >
                <template #targets="{item}">
                    <td>{{ formatTargets(item.targets) }}</td>
                </template>
                <template #runtime="{item}">
                    <td>{{ item.runtime ? __('products.productPlans.runtime.' + item.runtime) : '' }}</td>
                </template>

                <template
                    v-for="prefix in pricePointPrefixes"
                    :key="`${prefix}_price`"
                    #[`${prefix}_price`]="{item}"
                >
                    <td>
                        <template v-if="item[`${prefix}_price`]">
                            {{ item[`${prefix}_price`] }}<template v-if="item[`${prefix}_gross_price`]">
                                /{{ item[`${prefix}_gross_price`] }}
                            </template>
                        </template>
                    </td>
                </template>

                <template
                    v-for="prefix in pricePointPrefixes"
                    :key="`${prefix}_setup_fee`"
                    #[`${prefix}_setup_fee`]="{item}"
                >
                    <td>
                        <template v-if="item[`${prefix}_setup_fee`]">
                            {{ item[`${prefix}_setup_fee`] }}<template v-if="item[`${prefix}_gross_setup_fee`]">
                                /{{ item[`${prefix}_gross_setup_fee`] }}
                            </template>
                        </template>
                    </td>
                </template>

                <template
                    v-for="prefix in pricePointPrefixes"
                    :key="`${prefix}_contract_term`"
                    #[`${prefix}_contract_term`]="{item}"
                >
                    <td>
                        <template v-if="item[`${prefix}_contract_term`] !== null && item[`${prefix}_contract_term`] !== undefined">
                            {{ item[`${prefix}_contract_term`] }} {{ item[`${prefix}_contract_term_unit`] }}
                        </template>
                    </td>
                </template>

                <template
                    v-for="prefix in pricePointPrefixes"
                    :key="`${prefix}_notice_period`"
                    #[`${prefix}_notice_period`]="{item}"
                >
                    <td>
                        <template v-if="item[`${prefix}_notice_period`] !== null && item[`${prefix}_notice_period`] !== undefined">
                            {{ item[`${prefix}_notice_period`] }} {{ item[`${prefix}_notice_period_unit`] }}
                        </template>
                    </td>
                </template>

                <template
                    v-for="prefix in pricePointPrefixes"
                    :key="`${prefix}_discount_value`"
                    #[`${prefix}_discount_value`]="{item}"
                >
                    <td>
                        <template v-if="item[`${prefix}_discount_value`] !== null && item[`${prefix}_discount_value`] !== undefined">
                            {{ item[`${prefix}_discount_value`] }}<template v-if="item[`${prefix}_discount_type_key`] === 'percentage'">
                                %
                            </template><template v-if="item[`${prefix}_gross_discount_value`]">
                                /{{ item[`${prefix}_gross_discount_value`] }}
                            </template>
                        </template>
                    </td>
                </template>
            </DTable>
        </div>

        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script setup>
import { router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import ListViewModal from '~@/Components/ListView/ListViewModal'

const props = defineProps({
    product: {
        type: Object,
        default: () => {
            return {}
        }
    },
    plans: {
        type: Array,
        default: () => []
    },
    targets: {
        type: Array,
        default: () => []
    },
    marketRegions: {
        type: Array,
        default: () => []
    },
    currencies: {
        type: Array,
        default: () => []
    },
    runtimes: {
        type: Array,
        default: () => []
    },
    billingCycles: {
        type: Array,
        default: () => []
    },
    contractTerms: {
        type: Array,
        default: () => []
    },
    discountTypes: {
        type: Array,
        default: () => []
    },
    filterData: {
        type: Object,
        default: () => {
            return {}
        }
    },
    userListView: {
        type: Array,
        default: () => []
    }
})

const pricePointPrefixes = ['p1', 'p2', 'p3']

const strictNullKeys = pricePointPrefixes.flatMap((prefix) => [
    `${prefix}_contract_term`,
    `${prefix}_notice_period`,
    `${prefix}_discount_value`
])

const tableItems = computed(() =>
    (props.plans || []).map((item) => {
        const normalized = { ...item }

        Object.keys(normalized).forEach((key) => {
            if (normalized[key] === null && strictNullKeys.includes(key) === false) {
                normalized[key] = ''
            }
        })

        return normalized
    })
)

const filter = {
    search: props.filterData.search ?? null,
    targets: props.filterData.targets ?? [],
    marketRegions: props.filterData.marketRegions ?? [],
    currencies: props.filterData.currencies ?? [],
    runtimes: props.filterData.runtimes ?? [],
    billingCycles: props.filterData.billingCycles ?? [],
    contractTerms: props.filterData.contractTerms ?? [],
    discountTypes: props.filterData.discountTypes ?? []
}

function toOptions (source, selectedValues, cast) {
    const selected = (selectedValues || []).map((value) => cast ? cast(value) : value)

    return source.map((item) => {
        const value = cast ? cast(item.value) : item.value

        return { value, text: item.label, selected: selected.includes(value) }
    })
}

const targetOptions = computed(() => toOptions(props.targets, filter.targets, String))
const marketRegionOptions = computed(() => toOptions(props.marketRegions, filter.marketRegions, (v) => parseInt(v)))
const currencyOptions = computed(() => toOptions(props.currencies, filter.currencies, String))
const runtimeOptions = computed(() => toOptions(props.runtimes, filter.runtimes, String))
const billingCycleOptions = computed(() => toOptions(props.billingCycles, filter.billingCycles, (v) => parseInt(v)))
const contractTermOptions = computed(() => toOptions(props.contractTerms, filter.contractTerms, String))
const discountTypeOptions = computed(() => toOptions(props.discountTypes, filter.discountTypes, String))

const selectedTargets = computed(() => filter.targets.map((item) => String(item)))
const selectedMarketRegions = computed(() => filter.marketRegions.map((item) => parseInt(item)))
const selectedCurrencies = computed(() => filter.currencies.map((item) => String(item)))
const selectedRuntimes = computed(() => filter.runtimes.map((item) => String(item)))
const selectedBillingCycles = computed(() => filter.billingCycles.map((item) => parseInt(item)))
const selectedContractTerms = computed(() => filter.contractTerms.map((item) => String(item)))
const selectedDiscountTypes = computed(() => filter.discountTypes.map((item) => String(item)))

const showListViewModal = ref(false)

const getFields = computed(() => {
    const availableList = []

    props.userListView.forEach((item) => {
        if (item.show_column === true) {
            availableList.push({
                key: item.column,
                label: __('settings.list_view.' + item.list + '.' + item.column),
                sorter: false,
                _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
            })
        }
    })

    return availableList
})

function formatTargets (targets) {
    return (targets || [])
        .map((target) => __('products.productPlans.form.targets.' + target))
        .join(', ')
}

function updateSelectedTargets (values) {
    filter.targets = values
}
function updateSelectedMarketRegions (values) {
    filter.marketRegions = values
}
function updateSelectedCurrencies (values) {
    filter.currencies = values
}
function updateSelectedRuntimes (values) {
    filter.runtimes = values
}
function updateSelectedBillingCycles (values) {
    filter.billingCycles = values
}
function updateSelectedContractTerms (values) {
    filter.contractTerms = values
}
function updateSelectedDiscountTypes (values) {
    filter.discountTypes = values
}

function submitFilters () {
    const query = Object.fromEntries(Object.entries(filter).filter(([, v]) => v))

    router.get(route('products.products.plans-advanced-view', {
        product: props.product.id,
        _query: query,
        saveFilters: true
    }))
}

function resetFilters () {
    router.get(route('products.products.plans-advanced-view', {
        product: props.product.id,
        saveFilters: true,
        defaultFilters: true
    }))
}
</script>

<style lang="scss" scoped>
.list-configuration {
    display: flex;
    grid-gap: 10px;
    float: right;

    & > p {
        padding-top: 10px !important;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
    }
}
</style>
